import { Alias, MailboxEntryData } from '../../models/mailboxes.model';
import * as AliasActions from '../actions/alias.actions';
import * as MailboxActions from '../actions/mailbox.actions';
import { TechnicalError } from '../../models/errors.model';
import { SuccessCode } from '../../models/success.model';

export interface AliasesState {
  entries: AliasEntry;
  error: TechnicalError;
  success?: any;
}

export interface AliasEntry {
  [mbxId: string]: Alias[];
}

export const initialState: AliasesState = {
  entries: null,
  error: null,
  success: undefined,
};

export function reducer(
  state = initialState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  switch (action.type) {
    case MailboxActions.ActionTypes.LOADED:
      return loadedMailboxAliases(state, action);
    case AliasActions.ActionTypes.CREATE_SUCCESS:
      return createdMailboxAlias(state, action);
    case AliasActions.ActionTypes.REMOVE_SUCCESS:
      return removedMailboxAlias(state, action);
    case AliasActions.ActionTypes.REMOVE_FAIL:
      return failedRemovingMailboxAlias(state, action);
    case AliasActions.ActionTypes.CREATE_FAIL:
      return failedCreateMailboxAlias(state, action);
    case AliasActions.ActionTypes.RESET_ERROR:
      return resetError(state, action);
    case AliasActions.ActionTypes.RESET_SUCCESS:
      return resetSuccess(state, action);
    case AliasActions.ActionTypes.REMOVE:
    case AliasActions.ActionTypes.CREATE:
    default:
      return state;
  }
}

function loadedMailboxAliases(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  const aliasEntries = action.payload.mailboxes.reduce(
    (aliases: { [mbxId: string]: Alias[] }, mailbox: MailboxEntryData) =>
      !mailbox.aliases
        ? aliases
        : {
            ...aliases,
            [mailbox.mbxId]: mailbox.aliases,
          },
    {},
  );

  return {
    ...state,
    entries: aliasEntries,
  };
}

function removedMailboxAlias(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  const aliasEntries = Object.assign({}, state.entries);
  const aliasId = action.payload.aliasId;
  const filteredAliases = aliasEntries[action.payload.mbxId].filter(
    (alias) => alias.aliasId !== aliasId,
  );
  if (filteredAliases.length > 0) {
    aliasEntries[action.payload.mbxId] = filteredAliases;
  } else {
    delete aliasEntries[action.payload.mbxId];
  }

  return {
    ...state,
    success: {
      code: SuccessCode.AliasRemoved,
    },
    entries: aliasEntries,
  };
}

function createdMailboxAlias(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  const mailboxAliases = state.entries[action.payload.mbxId]
    ? [...state.entries[action.payload.mbxId]]
    : [];
  mailboxAliases.push(<Alias>{
    aliasId: action.payload.aliasId,
  });

  const aliasEntries = {
    ...state.entries,
    [action.payload.mbxId]: mailboxAliases,
  };

  return {
    ...state,
    success: {
      code: SuccessCode.AliasCreated,
    },
    entries: aliasEntries,
  };
}

function failedRemovingMailboxAlias(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  return {
    ...state,
    error: action.payload,
  };
}

function failedCreateMailboxAlias(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  return {
    ...state,
    error: action.payload,
  };
}

function resetSuccess(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  return {
    ...state,
    success: null,
  };
}

function resetError(
  state: AliasesState,
  action: AliasActions.Actions | MailboxActions.Actions,
): AliasesState {
  return {
    ...state,
    error: null,
  };
}

export const getAliasesEntries = (state: AliasesState) => state.entries;
export const getAliasModificationSuccess = (state: AliasesState) =>
  state.success;
export const getAliasModificationFailure = (state: AliasesState) => state.error;
